<template>
  <Portal
    :to="portalTarget"
    :disabled="isPortalDisabled"
    slim
  >
    <div
      ref="norticList"
      v-on-clickaway:mousedown="emitClickaway"
      class="nortic-list"
      :class="listClasses"
      :style="listStyles"
      :tabindex="tabIndex"
      @keydown.down.self.prevent="setFocusOnFirstChild"
      @keydown.up.self.prevent="setFocusOnLastChild"
      @keydown="emitKeydownEvent"
    >
      <slot name="header-container">
        <div
          v-if="hasHeaderSlot"
          class="header-container"
        >
          <slot name="header-content" />
        </div>
      </slot>
      <slot
        name="list-container"
        :items="items"
      >
        <ul
          ref="listContainer"
          class="list-container"
          :style="listContainerStyles"
          @scroll="throttledEmitScrollEvent"
        >
          <slot
            name="list-content"
            :items="items"
          >
            <template v-for="(item, index) in items">
              <NorticListItem
                :key="itemKeyMapper(item, index)"
                ref="items"
                :item-data="item"
                :label="itemLabelMapper(item, index)"
                :is-disabled="itemDisabledMapper(item, index)"
                :is-active="itemSelectedMapper(item, index)"
                :children="itemChildrenMapper(item, index)"
                :child-item-disabled-mapper="itemDisabledMapper"
                :child-item-key-mapper="itemKeyMapper"
                :child-item-selected-mapper="itemSelectedMapper"
                :child-item-children-mapper="itemChildrenMapper"
                :child-item-label-mapper="itemLabelMapper"
                @click="emitItemClickedEvent(item, index)"
                @child-click="emitItemClickedEvent"
                @item-has-focus="setFocusedRefIndex"
                @focus-next-item="focusNextItem"
                @focus-previous-item="focusPreviousItem"
              >
                <template #default="{ isActive, isDisabled, label, isParent, itemData, level }">
                  <slot
                    name="item-content"
                    :item="itemData"
                    :index="index"
                    :is-active="isActive"
                    :is-disabled="isDisabled"
                    :label="label"
                    :is-parent="isParent"
                    :level="level"
                  >
                    {{ label }}
                  </slot>
                </template>
              </NorticListItem>
            </template>
            <NorticListItem
              v-if="!hasItems"
              is-disabled
            >
              Inget tillgängligt
            </NorticListItem>
          </slot>
        </ul>
      </slot>
      <slot name="footer-container">
        <div
          v-if="hasFooterSlot"
          class="footer-container"
        >
          <slot name="footer-content" />
        </div>
      </slot>
    </div>
  </Portal>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2';
import NorticListItem from '@/components/common/data/nortic-list/NorticListItem.vue';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { Portal } from 'portal-vue';
import norticListMixin from '@/components/common/data/nortic-list/mixin/norticList';

export default {
  name: 'NorticList',
  components: { NorticListItem, Portal },
  mixins: [clickaway, norticListMixin],
  data() {
    return {
      popper: null,
      focusedRefIndex: -1,
    };
  },
  computed: {
    tabIndex() {
      return this.isDisabled
        ? '-1'
        : '0';
    },
    hasHeaderSlot() {
      return !!this.$slots['header-content'];
    },
    hasFooterSlot() {
      return !!this.$slots['footer-content'];
    },
    isPortalDisabled() {
      return !this.portalTarget;
    },
    listStyles() {
      return {
        position: this.relativeTo
          ? 'absolute'
          : null,
        zIndex: this.relativeTo
          ? this.zIndex
          : null,
        ...this.listStyle,
      };
    },
    listContainerStyles() {
      return {
        maxHeight: this.listMaxHeight,
        overflow: 'auto',
        ...this.customListContainerStyle,
      };
    },
    listClasses() {
      return {
        shadow: !!this.relativeTo,
      };
    },
    itemRefs() {
      return this.buildRefsRecursively(this.items, this.$refs.items);
    },
    hasItems() {
      return this.items && this.items.length > 0;
    },
  },
  watch: {
    focusedRefIndex(newRefIndex, oldRefIndex) {
      if (this.itemRefs[newRefIndex].hasAttribute('disabled')) {
        if ((newRefIndex > oldRefIndex && newRefIndex !== this.itemRefs.length - 1) || newRefIndex === 0) {
          this.focusNextItem();
        } else {
          this.focusPreviousItem();
        }
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      const relativeElement = this.getRelativeElement();

      if (relativeElement) {
        const {
          height,
          top,
          left,
          width,
        } = relativeElement.getBoundingClientRect();
        const scrollX = window.scrollX || window.pageXOffset;
        const scrollY = window.scrollY || window.pageYOffset;

        this.popper = this.calculatePosition(this.$refs.norticList, relativeElement, this, {
          width: `${width}px`,
          left: `${(scrollX + left)}px`,
          top: `${(scrollY + top + height)}px`,
        });
      }
    });
  },
  destroyed() {
    if (this.popper && typeof this.popper.destroy === 'function') {
      this.popper.destroy();
    }
  },
  methods: {
    buildRefsRecursively(items, ref) {
      return items.flatMap((item, index) => {
        const children = this.itemChildrenMapper(item);
        const hasChildren = Array.isArray(children) && children.length > 0;
        const childRefs = (hasChildren
          ? this.buildRefsRecursively(children, ref[index].$refs.children)
          : []);

        return [
          ref[index].$refs.norticListContainer,
          ...childRefs,
        ];
      });
    },
    getRelativeElement() {
      if (!this.relativeTo) { return null; }

      if (this.relativeTo instanceof Element) {
        return this.relativeTo;
      }

      if (isObject(this.relativeTo) && this.relativeTo.$el instanceof Element) {
        return this.relativeTo.$el;
      }

      if (isString(this.relativeTo)) {
        const el = document.querySelector(this.relativeTo);

        if (el instanceof Element) {
          return el;
        }
      }

      return null;
    },
    isListFocusedAndHasItemsRef() {
      return (this.$refs.norticList === document.activeElement) && this.$refs.items;
    },
    setFocusOnFirstChild() {
      if (this.isListFocusedAndHasItemsRef) {
        this.itemRefs[0].focus();
      }
    },
    setFocusOnLastChild() {
      if (this.isListFocusedAndHasItemsRef) {
        this.itemRefs[this.itemRefs.length - 1].focus();
      }
    },
    focusNextItem() {
      if (this.itemRefs[this.focusedRefIndex + 1]) {
        this.itemRefs[this.focusedRefIndex + 1].focus();
      }
    },
    focusPreviousItem() {
      if (this.itemRefs[this.focusedRefIndex - 1]) {
        this.itemRefs[this.focusedRefIndex - 1].focus();
      }
    },
    setFocusedRefIndex(focusedRef) {
      this.focusedRefIndex = this.itemRefs.findIndex((ref) => focusedRef === ref);
    },
  },
};
</script>

<style lang="scss" scoped>
  .nortic-list {
    &:focus {
      outline: none;
    }

    &.shadow {
      box-shadow: 0 0 0.25em 0 var(--shadow-color-hex);
    }

    display: inline-grid;
    border: 0.06em solid var(--input-border-hex);
    background: var(--input-bg-hex);
    overflow: auto;

    .header-container {
      padding: 1em;
      background: var(--primary-bg-hex);
      border-bottom: 0.06em solid var(--input-border-hex);
    }

    .list-container {
      display: grid;
      padding: 0;
      margin: 0;
    }

    .footer-container {
      padding: 1em;
      background: var(--primary-bg-hex);
      border-top: 0.06em solid var(--input-border-hex);
    }
  }
</style>
